import React, { useEffect, useState } from 'react';
import { X, Sparkles, AlertCircle, FilePlus, Wand2, Send, Loader2 } from 'lucide-react';
import { generateTextWithChatGpt } from '../../lib/services/chatGptService';
import { useOrgAiEnabled, AI_DISABLED_TOOLTIP } from '../../lib/hooks/useOrgAiEnabled';

interface ChatGptPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTextGenerated: (text: string) => void;
  /** HTML attualmente nell'editor descrizione: se vuoto, mostriamo solo i preset "nuovo". */
  currentText?: string;
  serviceTitle?: string;
  translations?: any;
}

type Mode = 'new' | 'improve';

interface PromptPreset {
  id: string;
  label: string;
  description: string;
  prompt: string;
}

/**
 * Drawer animato per la generazione testo via AI nella descrizione offerta.
 *
 * Sostituisce il vecchio modale centrato con uno slide-in da destra (pattern
 * Notion-like) che:
 *  - si apre/chiude con transizione 250ms
 *  - propone DUE modalità: "Nuova" (genera da zero) e "Migliora" (riscrive
 *    il testo attualmente in editor). La modalità "Migliora" è disabilitata
 *    quando l'editor è vuoto.
 *  - mostra una griglia di preset chip selezionabili contestuali alla
 *    modalità scelta (ogni preset compila lo stesso textarea)
 *  - l'utente può poi editare/aggiungere al prompt prima di lanciare
 */

const NEW_PRESETS: PromptPreset[] = [
  {
    id: 'sintetica',
    label: 'Sintetica',
    description: '2-3 frasi che presentano valore + cliente',
    prompt: "Scrivi un'introduzione sintetica (2-3 frasi) che presenti l'offerta evidenziando il suo valore principale per il cliente.",
  },
  {
    id: 'problema-soluzione',
    label: 'Problema → Soluzione',
    description: 'Apre col problema, propone la soluzione',
    prompt: 'Crea un\'introduzione che catturi l\'attenzione partendo dal problema del cliente e mostrando come la nostra offerta lo risolve.',
  },
  {
    id: 'benefici-chiave',
    label: 'Benefici chiave',
    description: 'Lista breve dei vantaggi misurabili',
    prompt: 'Scrivi un breve riepilogo dei vantaggi chiave di questa proposta commerciale, evidenziando i risultati concreti che il cliente otterrà.',
  },
  {
    id: 'differenziante',
    label: 'Differenziante',
    description: 'Cosa ci rende unici vs concorrenza',
    prompt: 'Introduci l\'offerta sottolineando l\'unicità della nostra soluzione rispetto alla concorrenza, con tono professionale ma diretto.',
  },
];

const IMPROVE_PRESETS: PromptPreset[] = [
  {
    id: 'piu-chiaro',
    label: 'Più chiaro',
    description: 'Stessa idea, linguaggio più semplice',
    prompt: 'Riscrivi il testo esistente mantenendo intatto il contenuto ma rendendolo più chiaro, scorrevole e immediato.',
  },
  {
    id: 'piu-persuasivo',
    label: 'Più persuasivo',
    description: 'Aggiunge call-to-action e tono assertivo',
    prompt: 'Riformula il testo in modo più persuasivo, organizzandolo per evidenziare "Cosa offriamo", "Vantaggi" e "Perché sceglierci".',
  },
  {
    id: 'piu-strutturato',
    label: 'Più strutturato',
    description: 'Titoli, bullet, formattazione Markdown',
    prompt: 'Aggiungi struttura al testo esistente usando titoli in grassetto, sezioni e punti elenco per i vantaggi, mantenendo lo stesso contenuto.',
  },
  {
    id: 'piu-breve',
    label: 'Più breve',
    description: 'Riduci alla metà mantenendo i punti chiave',
    prompt: 'Riduci il testo esistente alla metà mantenendo solo i punti chiave essenziali e il tono originale.',
  },
];

const ChatGptPromptModal: React.FC<ChatGptPromptModalProps> = ({
  isOpen,
  onClose,
  onTextGenerated,
  currentText = '',
  serviceTitle = '',
  translations: t = {},
}) => {
  const [prompt, setPrompt] = useState('');
  const [activePresetId, setActivePresetId] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Interruttore IA dell'azienda: spento = pulsante Genera disabilitato.
  const aiEnabled = useOrgAiEnabled();

  const hasCurrentText = currentText && currentText.replace(/<[^>]*>/g, '').trim().length > 0;
  const [mode, setMode] = useState<Mode>(hasCurrentText ? 'improve' : 'new');

  // Mode mutua quando si apre/cambia il testo corrente.
  useEffect(() => {
    if (isOpen) setMode(hasCurrentText ? 'improve' : 'new');
  }, [isOpen, hasCurrentText]);

  // Reset prompt + preset quando cambia la mode (evita confusione tra preset cross-mode).
  useEffect(() => {
    setActivePresetId(null);
    setPrompt('');
  }, [mode]);

  const presets = mode === 'new' ? NEW_PRESETS : IMPROVE_PRESETS;

  const handleSelectPreset = (preset: PromptPreset) => {
    setActivePresetId(preset.id);
    setPrompt(preset.prompt);
  };

  const handleSubmit = async () => {
    if (!prompt.trim()) {
      setError(t.promptRequired || 'Scegli un preset o scrivi un prompt');
      return;
    }
    setError(null);
    setIsGenerating(true);
    try {
      // Per la mode "new" passiamo currentText vuoto così l'AI non viene
      // influenzata dal testo precedente; per "improve" passiamo invece tutto.
      const textForAi = mode === 'improve' ? currentText : '';
      const result = await generateTextWithChatGpt(prompt, textForAi, serviceTitle);
      if (result.error) {
        setError(result.error);
      } else if (result.text) {
        onTextGenerated(result.text);
        onClose();
      } else {
        setError(t.emptyResponseError || 'Nessun testo generato. Riprova con un prompt diverso.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore durante la generazione del testo');
    } finally {
      setIsGenerating(false);
    }
  };

  // Render anche quando isOpen=false per permettere all'animazione di chiusura
  // di completarsi prima dello unmount. translate-x-full quando chiuso.
  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[9990] bg-black/30 transition-opacity duration-200 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Drawer (slide-in da destra) */}
      <div
        className={`fixed top-0 right-0 bottom-0 z-[9991] w-full max-w-md bg-white dark:bg-gray-900 shadow-2xl flex flex-col transition-transform duration-250 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ transitionDuration: '250ms' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-black/[0.06] dark:border-white/[0.08]">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-md bg-purple-500/15 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <div className="text-[14px] font-semibold text-black dark:text-white">
                {t.generateWithAI || 'Genera con IA'}
              </div>
              <div className="text-[11px] text-black/55 dark:text-white/55">
                Descrizione offerta
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-black/55 dark:text-white/55 hover:text-black dark:hover:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.06] rounded-md transition-colors"
            aria-label="Chiudi"
          >
            <X size={18} />
          </button>
        </div>

        {/* Mode switcher: Nuova vs Migliora */}
        <div className="px-4 pt-3 pb-2">
          <div className="grid grid-cols-2 gap-1.5 p-1 bg-black/[0.03] dark:bg-white/[0.04] rounded-lg">
            <ModeButton
              active={mode === 'new'}
              onClick={() => setMode('new')}
              Icon={FilePlus}
              label="Nuova"
              hint="Genera da zero"
            />
            <ModeButton
              active={mode === 'improve'}
              onClick={() => hasCurrentText && setMode('improve')}
              disabled={!hasCurrentText}
              Icon={Wand2}
              label="Migliora"
              hint={hasCurrentText ? 'Riscrivi quello attuale' : 'Nessun testo attuale'}
            />
          </div>
        </div>

        {/* Body scrollabile */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
          {/* Contesto: titolo servizio + (eventuale) testo attuale */}
          {(serviceTitle || (mode === 'improve' && hasCurrentText)) && (
            <div className="space-y-2 text-[12px]">
              {serviceTitle && (
                <div>
                  <div className="text-[10px] uppercase tracking-wide font-medium text-black/45 dark:text-white/45 mb-0.5">
                    Titolo offerta
                  </div>
                  <div className="text-black/80 dark:text-white/80">{serviceTitle}</div>
                </div>
              )}
              {mode === 'improve' && hasCurrentText && (
                <div>
                  <div className="text-[10px] uppercase tracking-wide font-medium text-black/45 dark:text-white/45 mb-0.5">
                    Testo attuale
                  </div>
                  <div className="max-h-24 overflow-y-auto p-2 bg-black/[0.025] dark:bg-white/[0.03] rounded text-black/65 dark:text-white/65 prose prose-sm dark:prose-invert max-w-none">
                    <div dangerouslySetInnerHTML={{ __html: currentText }} />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Preset chips */}
          <div>
            <div className="text-[10px] uppercase tracking-wide font-medium text-black/45 dark:text-white/45 mb-1.5">
              Preset
            </div>
            <div className="grid grid-cols-1 gap-1.5">
              {presets.map(preset => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => handleSelectPreset(preset)}
                  className={`text-left px-3 py-2 rounded-md border transition-all ${
                    activePresetId === preset.id
                      ? 'border-purple-400 dark:border-purple-500 bg-purple-50 dark:bg-purple-900/20 shadow-sm'
                      : 'border-black/[0.06] dark:border-white/[0.08] bg-white dark:bg-white/[0.02] hover:border-black/[0.15] dark:hover:border-white/[0.18] hover:bg-black/[0.02]'
                  }`}
                >
                  <div className="text-[13px] font-medium text-black dark:text-white">{preset.label}</div>
                  <div className="text-[11px] text-black/55 dark:text-white/55 mt-0.5">{preset.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Custom prompt textarea */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label htmlFor="ai-prompt" className="text-[10px] uppercase tracking-wide font-medium text-black/45 dark:text-white/45">
                Prompt
              </label>
              {prompt && (
                <button
                  type="button"
                  onClick={() => { setPrompt(''); setActivePresetId(null); }}
                  className="text-[10px] text-black/45 dark:text-white/45 hover:text-black/70 dark:hover:text-white/70 transition-colors"
                >
                  Cancella
                </button>
              )}
            </div>
            <textarea
              id="ai-prompt"
              rows={3}
              value={prompt}
              onChange={(e) => { setPrompt(e.target.value); setActivePresetId(null); }}
              placeholder={mode === 'new'
                ? 'Descrivi cosa vuoi generare…'
                : 'Descrivi come migliorare il testo attuale…'}
              className="w-full p-2.5 text-[13px] bg-white dark:bg-white/[0.03] border border-black/[0.08] dark:border-white/[0.1] rounded-md text-black dark:text-white placeholder-black/30 dark:placeholder-white/30 focus:outline-none focus:border-purple-400 dark:focus:border-purple-500 transition-colors resize-none"
            />
          </div>

          {error && (
            <div className="p-2.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-md flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-[12px] text-red-600 dark:text-red-300">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-black/[0.06] dark:border-white/[0.08] flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-[13px] font-medium text-black/65 dark:text-white/65 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] rounded-md transition-colors"
          >
            {t.cancel || 'Annulla'}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isGenerating || !prompt.trim() || !aiEnabled}
            title={!aiEnabled ? AI_DISABLED_TOOLTIP : undefined}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 dark:disabled:bg-purple-900/40 text-white rounded-md transition-colors"
          >
            {isGenerating ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {t.generating || 'Generazione…'}
              </>
            ) : (
              <>
                <Send className="h-3.5 w-3.5" />
                {t.generate || 'Genera'}
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
};

interface ModeButtonProps {
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
  Icon: React.ComponentType<{ className?: string }>;
  label: string;
  hint: string;
}

function ModeButton({ active, onClick, disabled, Icon, label, hint }: ModeButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center gap-2 px-3 py-2 rounded-md transition-all text-left ${
        active
          ? 'bg-white dark:bg-white/[0.08] shadow-sm'
          : 'hover:bg-white/60 dark:hover:bg-white/[0.05]'
      } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
    >
      <Icon className={`h-4 w-4 flex-shrink-0 ${active ? 'text-purple-600 dark:text-purple-400' : 'text-black/55 dark:text-white/55'}`} />
      <div className="min-w-0">
        <div className={`text-[12px] font-medium ${active ? 'text-black dark:text-white' : 'text-black/70 dark:text-white/70'}`}>
          {label}
        </div>
        <div className="text-[10px] text-black/45 dark:text-white/45 truncate">{hint}</div>
      </div>
    </button>
  );
}

export default ChatGptPromptModal;
